import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/** Grille démo dédiée — ≠ sudoku de l’utilisateur. */
const DEMO_SIZE = 9;
const DEMO_DIGIT = '5';
const DEMO_TARGET = { r: 4, c: 4 };

/**
 * Vraie grille (extrait d’un sudoku classique) : la case cible est la seule vide
 * de sa colonne, de sa ligne et de son carré 3×3. Les autres carrés ont 2–3 vides.
 */
const DEMO_GIVENS = [
  ['', '3', '4', '', '7', '8', '9', '', '2'],
  ['6', '', '2', '1', '9', '', '', '4', '8'],
  ['1', '9', '', '', '4', '2', '5', '6', ''],
  ['', '5', '', '7', '6', '1', '4', '', '3'],
  ['4', '2', '6', '8', '', '3', '7', '9', '1'],
  ['7', '', '3', '9', '2', '4', '', '5', ''],
  ['', '6', '1', '5', '3', '', '2', '', '4'],
  ['2', '', '7', '', '1', '9', '6', '3', ''],
  ['3', '4', '', '2', '8', '', '', '7', '9'],
];

function DemoMouse({ leftPressed = false }) {
  return (
    <div className="flex flex-col items-center gap-1.5" aria-hidden>
      <svg viewBox="0 0 72 110" className="w-[6.375rem] h-[9.75rem] drop-shadow-md" role="img">
        <title>Souris</title>
        <rect x="8" y="38" width="56" height="64" rx="28" fill="#e8eaed" stroke="#9aa0a6" strokeWidth="2" />
        <path
          d="M10 38 h24 v28 a4 4 0 0 1 -4 4 H14 a4 4 0 0 1 -4 -4 Z"
          fill={leftPressed ? '#f76f4b' : '#ffffff'}
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <path
          d="M38 38 h24 v28 a4 4 0 0 1 -4 4 H42 a4 4 0 0 1 -4 -4 Z"
          fill="#ffffff"
          stroke="#9aa0a6"
          strokeWidth="2"
        />
        <rect x="31" y="44" width="10" height="18" rx="5" fill="#bdc1c6" stroke="#9aa0a6" strokeWidth="1.5" />
        <line x1="36" y1="38" x2="36" y2="68" stroke="#9aa0a6" strokeWidth="2" />
      </svg>
      <p className="text-xs text-gray-400 text-center leading-tight max-w-[7rem]">
        {leftPressed ? 'Clic gauche' : 'Souris'}
      </p>
    </div>
  );
}

function CursorArrow() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" className="drop-shadow-lg" aria-hidden>
      <path
        d="M4 3l1 16 5-5 4 8 2-1-4-8 8-1z"
        fill="#111827"
        stroke="#fff"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function emptyGrid() {
  return DEMO_GIVENS.map((row) => row.map((cell) => cell || ''));
}

function inFocus(r, c, mode, target) {
  if (!mode || !target) return false;
  if (mode === 'col') return c === target.c;
  if (mode === 'row') return r === target.r;
  if (mode === 'box') {
    const br = Math.floor(target.r / 3) * 3;
    const bc = Math.floor(target.c / 3) * 3;
    return r >= br && r < br + 3 && c >= bc && c < bc + 3;
  }
  return false;
}

/**
 * Démo sudoku : sélection d’un chiffre dans la barre, placement, puis
 * mise en évidence colonne → ligne → carré 3×3 (chiffre une seule fois).
 */
export default function SudokuIntroDemo({ onClose, elapsedLabel = '00:00' }) {
  const [grid, setGrid] = useState(emptyGrid);
  const [selectedDigit, setSelectedDigit] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [focusMode, setFocusMode] = useState(null); // 'col' | 'row' | 'box' | null
  const [caption, setCaption] = useState('');
  const [mouseLeft, setMouseLeft] = useState(false);
  const [cursor, setCursor] = useState(null);
  const wrapRef = useRef(null);
  const gridRef = useRef(null);
  const digitsRef = useRef(null);
  const cursorPosRef = useRef({ x: 0, y: 0 });
  const cancelledRef = useRef(false);
  const [pane, setPane] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const dialog = el.closest('[data-game-season-dialog]');
      const scroller = dialog?.querySelector('.overflow-y-auto') || el.closest('.overflow-y-auto');
      const bottom = scroller
        ? scroller.getBoundingClientRect().bottom
        : window.innerHeight - 16;
      const top = el.getBoundingClientRect().top;
      setPane({
        w: Math.max(160, el.clientWidth),
        h: Math.max(160, bottom - top - 16),
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    const timers = [];
    let rafId = 0;

    const wait = (ms) => new Promise((resolve) => {
      const id = window.setTimeout(resolve, ms);
      timers.push(id);
    });

    const lerp = (a, b, t) => a + (b - a) * t;
    const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

    const setCursorPos = (pos) => {
      cursorPosRef.current = pos;
      setCursor(pos);
    };

    const animateCursor = (from, to, ms) => new Promise((resolve) => {
      const start = performance.now();
      const tick = (now) => {
        if (cancelledRef.current) {
          resolve();
          return;
        }
        const raw = Math.min(1, (now - start) / Math.max(1, ms));
        const t = ease(raw);
        setCursorPos({
          x: lerp(from.x, to.x, t),
          y: lerp(from.y, to.y, t),
        });
        if (raw < 1) rafId = requestAnimationFrame(tick);
        else resolve();
      };
      rafId = requestAnimationFrame(tick);
    });

    const elCenter = (root, selector) => {
      const node = root?.querySelector?.(selector);
      if (!node) return { x: 0, y: 0 };
      const rect = node.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    };

    const clickAt = async (target) => {
      const from = cursorPosRef.current?.x
        ? cursorPosRef.current
        : { x: target.x - 40, y: target.y - 30 };
      await animateCursor(from, target, 420);
      if (cancelledRef.current) return;
      setMouseLeft(true);
      await wait(180);
      setMouseLeft(false);
      await wait(120);
    };

    const resetAll = () => {
      setGrid(emptyGrid());
      setSelectedDigit(null);
      setSelectedCell(null);
      setFocusMode(null);
      setCaption('');
      setMouseLeft(false);
      setCursor(null);
    };

    const loopOnce = async () => {
      resetAll();
      await wait(500);
      if (cancelledRef.current) return;

      setCaption('Sélectionnez un chiffre dans la barre');
      const digitTarget = elCenter(digitsRef.current, `[data-demo-digit="${DEMO_DIGIT}"]`);
      await clickAt(digitTarget);
      if (cancelledRef.current) return;
      setSelectedDigit(DEMO_DIGIT);
      await wait(450);

      setCaption('Placez-le dans une case libre');
      const cellTarget = elCenter(
        gridRef.current,
        `[data-demo-cell="${DEMO_TARGET.r}-${DEMO_TARGET.c}"]`,
      );
      await clickAt(cellTarget);
      if (cancelledRef.current) return;
      setSelectedCell({ ...DEMO_TARGET });
      setGrid((prev) => prev.map((row, r) => (
        r === DEMO_TARGET.r
          ? row.map((cell, c) => (c === DEMO_TARGET.c ? DEMO_DIGIT : cell))
          : row
      )));
      await wait(700);
      if (cancelledRef.current) return;

      setCursor(null);
      setMouseLeft(false);

      setCaption(`Colonne complète : le ${DEMO_DIGIT} n’y apparaît qu’une fois`);
      setFocusMode('col');
      await wait(3200);
      if (cancelledRef.current) return;

      setCaption(`Ligne complète : le ${DEMO_DIGIT} n’y apparaît qu’une fois`);
      setFocusMode('row');
      await wait(3200);
      if (cancelledRef.current) return;

      setCaption(`Carré 3×3 complet : le ${DEMO_DIGIT} n’y apparaît qu’une fois`);
      setFocusMode('box');
      await wait(3600);
      if (cancelledRef.current) return;

      setFocusMode(null);
      setCaption('');
      await wait(500);
    };

    const run = async () => {
      await wait(250);
      while (!cancelledRef.current) {
        await loopOnce();
      }
    };
    run();

    return () => {
      cancelledRef.current = true;
      timers.forEach((id) => window.clearTimeout(id));
      if (rafId) cancelAnimationFrame(rafId);
      setCursor(null);
      setMouseLeft(false);
    };
  }, []);

  const gap = 0;
  const twoThirdsW = Math.max(180, Math.floor(pane.w * (2 / 3)));
  const digitColW = 52;
  const gridAvailW = Math.max(140, twoThirdsW - digitColW - 16);
  const cellFromW = gridAvailW / DEMO_SIZE;
  const cellFromH = Math.max(160, pane.h - 48) / DEMO_SIZE;
  const cellPx = Math.max(22, Math.min(44, Math.floor(Math.min(cellFromW, cellFromH) || 28)));
  const fontPx = Math.max(11, Math.round(cellPx * 0.48));
  const gridW = cellPx * DEMO_SIZE;
  const boxR = 3;
  const boxC = 3;

  return (
    <div
      className="relative rounded-md border border-orange-300/60 dark:border-orange-800/60 bg-white/95 dark:bg-gray-950/95 p-3"
      role="dialog"
      aria-label="Démonstration du sudoku"
    >
      <div className="relative flex items-center justify-center mb-2 min-h-[2rem]">
        <p className="text-lg font-semibold uppercase tracking-wide text-[#f76f4b]">Démo</p>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 shrink-0 rounded-md bg-[#f76f4b] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#e85f3a]"
        >
          <X size={14} />
          Fermer
        </button>
      </div>

      <p className="text-sm font-mono font-bold text-[#f76f4b] mb-2 tabular-nums">{elapsedLabel}</p>
      {caption ? (
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-2 min-h-[1rem]">{caption}</p>
      ) : (
        <p className="text-xs text-transparent mb-2 min-h-[1rem]">.</p>
      )}

      <div
        ref={wrapRef}
        className="w-full min-w-0 pointer-events-none select-none grid gap-3 items-stretch"
        style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}
      >
        <div className="min-w-0 flex flex-row flex-wrap sm:flex-nowrap gap-3 items-start justify-center sm:justify-start">
          <div ref={gridRef} className="shrink-0">
            <div
              className="inline-grid rounded-sm overflow-hidden border-2 border-gray-800 dark:border-gray-200"
              style={{ gridTemplateColumns: `repeat(${DEMO_SIZE}, ${cellPx}px)`, width: gridW }}
            >
              {grid.map((row, r) =>
                row.map((cell, c) => {
                  const boxRight = (c + 1) % boxC === 0 && c < DEMO_SIZE - 1;
                  const boxBottom = (r + 1) % boxR === 0 && r < DEMO_SIZE - 1;
                  const given = !!DEMO_GIVENS[r][c];
                  const locked = given || (r === DEMO_TARGET.r && c === DEMO_TARGET.c && cell === DEMO_DIGIT);
                  const match = !!(selectedDigit && cell === selectedDigit);
                  const selected = selectedCell?.r === r && selectedCell?.c === c;
                  const focused = inFocus(r, c, focusMode, DEMO_TARGET);
                  const dimmed = focusMode && !focused;
                  const digitHit = focused && cell === DEMO_DIGIT;
                  return (
                    <div
                      key={`${r}-${c}`}
                      data-demo-cell={`${r}-${c}`}
                      className={`text-center font-semibold relative flex items-center justify-center transition-colors ${
                        digitHit
                          ? 'bg-[#f76f4b] text-white'
                          : focused
                            ? 'bg-orange-100 dark:bg-orange-950/50 text-gray-800 dark:text-gray-100'
                            : match
                              ? locked
                                ? 'bg-orange-200 dark:bg-orange-900/60 text-gray-800 dark:text-gray-100'
                                : 'bg-orange-100 dark:bg-orange-950/50 text-gray-800 dark:text-gray-100'
                              : locked
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                                : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100'
                      } ${dimmed ? 'opacity-25' : ''} ${selected ? 'z-[1]' : ''} ${
                        boxRight
                          ? 'border-r-2 border-r-gray-800 dark:border-r-gray-200'
                          : 'border-r border-r-gray-300 dark:border-r-gray-600'
                      } ${
                        boxBottom
                          ? 'border-b-2 border-b-gray-800 dark:border-b-gray-200'
                          : 'border-b border-b-gray-300 dark:border-b-gray-600'
                      }`}
                      style={{
                        width: cellPx,
                        height: cellPx,
                        fontSize: fontPx,
                        boxShadow: selected && !focusMode ? 'inset 0 0 0 3px #f76f4b' : undefined,
                      }}
                    >
                      {cell}
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div ref={digitsRef} className="flex flex-col items-center gap-1.5 shrink-0 pt-1">
            {Array.from({ length: DEMO_SIZE }, (_, i) => String(i + 1)).map((n) => (
              <div
                key={n}
                data-demo-digit={n}
                className={`h-8 w-8 rounded-md text-sm font-bold border flex items-center justify-center ${
                  selectedDigit === n
                    ? 'bg-[#f76f4b] text-white border-[#f76f4b]'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600'
                }`}
              >
                {n}
              </div>
            ))}
          </div>
        </div>
        <div className="min-w-0 flex items-center justify-center self-stretch">
          <DemoMouse leftPressed={mouseLeft} />
        </div>
      </div>

      {cursor && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed pointer-events-none z-[35000]"
            style={{ left: cursor.x, top: cursor.y, transform: 'translate(-2px, -2px)' }}
          >
            <CursorArrow />
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
